import { clearDraftV1, copyApprovedV1, loadNewDraftV1 } from 'client/actions/DocumentsV1/draftV1/draftV1Actions'
import { saveDraftThunkV1, updateApprovedThunkV1 } from 'client/actions/DocumentsV1/draftV1/draftV1CreateUpdateThunks'
import { loadApprovedThunkV1, loadDraftThunkV1 } from 'client/actions/DocumentsV1/draftV1/draftV1InitializationThunks'
import { fetchPreviewForDraftV1, setNewCustomerV1Thunk } from 'client/actions/DocumentsV1/draftV1/draftV1Thunks'
import { defaultLine } from 'client/actions/helpers/draft'
import { type IThunkAction } from 'client/actions/index'
import { navigateAddModal, navigateRemoveModal, setNavigationPath } from 'client/actions/navigation'
import { hideGeneratingDocumentSpinner, showGeneratingDocumentSpinner } from 'client/actions/ongoing'
import * as deliverynoteApi from 'client/api/deliverynote'
import * as documentApi from 'client/api/document'
import * as api from 'client/api/draft'
import { type DeliverynoteNumberResponseInterface, getNextNumberV1 } from 'client/api/invoiceNumber'
import {
  type DeliverynoteDraftInterface,
  type DeliverynoteInterface,
  DeliverynoteType,
} from 'client/components/DocumentsV1/Deliverynote/DeliverynoteInterface'
import { type ReceiptDraftInterface } from 'client/components/DocumentsV1/Receipt/ReceiptInterface'
import { type IGraphQl } from 'client/graphQueries/IGraphQl'
import { downloadPdfByIdV1 } from 'client/hooks/useDownloadPdf'
import { DocumentTypes } from 'client/Interfaces/Draft/DraftInterface'
import { type ConfigInterface, type Language } from 'client/Interfaces/Draft/v1/DraftInterface'
import { type EditedDraftV1Interface } from 'client/Interfaces/Draft/v1/EditedDraftV1Interface'
import { type ModalInterface } from 'client/Interfaces/ModalInterface'
import { type StateInterface } from 'client/Interfaces/StateInterface'
import { getDraftV1Selector } from 'client/reducers/draftV1Reducer'
import { settingsSelector, tradeNameV1Selector } from 'client/reducers/settings'
import moment from 'moment'

export const createOrUpdateDeliverynoteDraftThenNavigateToListV1 = (): IThunkAction => {
  return async (dispatch, getState: () => StateInterface) => {
    try {
      dispatch(showGeneratingDocumentSpinner())
      await dispatch(saveDraftThunkV1(DocumentTypes.DELIVERYNOTE))
      const draft = getDraftV1Selector(getState())

      if (!draft.invalidFields && draft.id) {
        dispatch(clearDraftV1())
        dispatch(setNavigationPath(`/deliverynotes/drafts/${draft.id}`))
      }
    } catch (e) {
      console.error('Error occurred while saving draft', e)
      dispatch(setNavigationPath('/deliverynotes'))
    } finally {
      dispatch(hideGeneratingDocumentSpinner())
    }
  }
}

export const approveDeliverynoteDraft = (): IThunkAction<
  null,
  string | DeliverynoteDraftInterface,
  Promise<DeliverynoteInterface>
> => {
  return async (dispatch, getState) => {
    await dispatch(saveDraftThunkV1(DocumentTypes.DELIVERYNOTE))
    const draft = getDraftV1Selector(getState())
    const document = await api.approveDraftV1(DocumentTypes.DELIVERYNOTE, draft.id)
    if (!draft.invalidFields && draft.id) {
      await dispatch(loadApprovedThunkV1(DocumentTypes.DELIVERYNOTE, document.id))
    }
    return document
  }
}

export const approveDraftOrUpdateDeliverynoteThenNavigateToListV1 = (downloadPdf = false): IThunkAction => {
  return async (dispatch, getState): Promise<void> => {
    try {
      dispatch(showGeneratingDocumentSpinner())
      let draft = getDraftV1Selector(getState())
      if (draft && draft.documentType === DeliverynoteType.approved) {
        await dispatch(updateApprovedThunkV1(DocumentTypes.DELIVERYNOTE))
      } else {
        await dispatch(approveDeliverynoteDraft())
      }
      draft = getDraftV1Selector(getState())
      if (draft.id) {
        if (downloadPdf) {
          const state = getState()
          const { token, tradeNames } = {
            token: state.token,
            tradeNames: settingsSelector(state).values?.company.tradeNames,
          }
          await downloadPdfByIdV1(DocumentTypes.DELIVERYNOTE, draft.id, token, tradeNames)
        }
        dispatch(setNavigationPath(`/deliverynotes/approved/${draft.id}`))
      }
    } catch (e) {
      console.error('Error occurred while approving or editing document', e)
      dispatch(setNavigationPath('/deliverynotes'))
    } finally {
      dispatch(hideGeneratingDocumentSpinner())
      dispatch(clearDraftV1())
    }
  }
}

export const showDeliverynoteDraft = (id: number): IThunkAction<null, ModalInterface | string> => {
  return (dispatch) => {
    dispatch(
      navigateAddModal({
        component: 'ShowDocumentModal',
        props: {
          content: documentApi.getDraftDocumentHTMLV1(DocumentTypes.DELIVERYNOTE, id),
          onClose: () => dispatch(navigateRemoveModal('ShowDocumentModal')),
        },
      }),
    )
  }
}

export const showDeliveryNote = (id: number): IThunkAction<null, ModalInterface | string> => {
  return (dispatch) => {
    dispatch(
      navigateAddModal({
        component: 'ShowDocumentModal',
        props: {
          content: documentApi.getDocumentHTMLV1(DocumentTypes.DELIVERYNOTE, id),
          onClose: () => dispatch(navigateRemoveModal('ShowDocumentModal')),
        },
      }),
    )
  }
}

export const previewAndSendDeliverynote = (id: number): IThunkAction => {
  return async (dispatch) => {
    await dispatch(loadDraftThunkV1(DocumentTypes.DELIVERYNOTE, id))
    await dispatch(fetchPreviewForDraftV1(DocumentTypes.DELIVERYNOTE))
  }
}

export const copyDeliverynote = (
  id: number,
): IThunkAction<null, ReceiptDraftInterface | DeliverynoteDraftInterface | string> => {
  return async (dispatch) => {
    const copiedApproved = await deliverynoteApi.getDeliverynote(id)
    delete copiedApproved.id
    delete copiedApproved.revisionId
    delete copiedApproved.status
    const { number: deliverynoteNumber } = await getNextNumberV1<DeliverynoteNumberResponseInterface>(
      DocumentTypes.DELIVERYNOTE,
    )
    dispatch(
      copyApprovedV1({
        ...copiedApproved,
        documentType: DeliverynoteType.draft,
        deliverynoteNumber: deliverynoteNumber.toString(),
      }),
    )
    dispatch(setNewCustomerV1Thunk(copiedApproved?.customer))
    dispatch(setNavigationPath('/editor/deliverynotes/drafts/new'))
  }
}

export const loadNewDeliverynoteDraftThunk = (
  config: IGraphQl<ConfigInterface>,
): IThunkAction<null, EditedDraftV1Interface, Promise<void>> => {
  return async (dispatch, getState) => {
    const { settings, company: companyWithoutTradename } = getState()
    const { fields } = config
    const { title, ...fieldsForSettings } = fields
    const configForSettings = { ...config, fields: fieldsForSettings }
    const timeZone = { name: 'timeZone', visible: true, value: Intl.DateTimeFormat().resolvedOptions().timeZone }
    configForSettings.fields.timeZone = timeZone
    const company = tradeNameV1Selector(getState(), fields.sender?.value)
    const { number: deliverynoteNumber } = await getNextNumberV1<DeliverynoteNumberResponseInterface>(
      DocumentTypes.DELIVERYNOTE,
    )

    const draft = {
      deliverynoteNumber,
      company,
      config: configForSettings,
      customer: null,
      documentType: DeliverynoteType.draft,
      description: fields.description.value,
      footerText: fields.footerText.value,
      title: title.value,
      lines: [defaultLine(settings)],
      language: companyWithoutTradename.language as Language,
      issueDate: moment().toISOString(),
    }
    dispatch(loadNewDraftV1(draft))
  }
}
